import { useId, useMemo } from 'react';
import { cn } from '@/lib/utils';

type MoodScale = 'low' | 'medium' | 'high' | 'unknown';

const COLOR_STOP_1 = '#FF0808'; // Rosso
const COLOR_STOP_2 = '#FFE500'; // Arancio
const COLOR_STOP_3 = '#4CE820'; // Giallo
const COLOR_STOP_4 = '#1C7BFF'; // Verde/azzurro

const SCALE_CONFIG: Record<MoodScale, { label: string; color: string }> = {
  low: { label: 'Basso', color: COLOR_STOP_1 },
  medium: { label: 'Stabile', color: COLOR_STOP_3 },
  high: { label: 'Alto', color: COLOR_STOP_4 },
  unknown: { label: 'N/D', color: '#94a3b8' },
};

const GAUGE_SIZE = 108;
const STROKE_WIDTH = 12;
const RADIUS = (GAUGE_SIZE - STROKE_WIDTH) / 2;
const CENTER = GAUGE_SIZE / 2;
const SEGMENTS = 12;
const START_ANGLE = -225;
const END_ANGLE = 45;
const ARC_RANGE = END_ANGLE - START_ANGLE;

interface GradientSegment {
  id: string;
  path: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  startColor: string;
  endColor: string;
}

const clampValue = (value: number): number => {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 10) return 10;
  return value;
};

const resolveScale = (value: number | null | undefined): MoodScale => {
  if (value == null || Number.isNaN(value)) return 'unknown';
  if (value <= 3) return 'low';
  if (value <= 6) return 'medium';
  return 'high';
};

const hexToRgb = (hex: string) => {
  const value = hex.replace('#', '');
  const bigint = Number.parseInt(value, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

const rgbToHex = (r: number, g: number, b: number) =>
  `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;

const interpolateColor = (from: string, to: string, t: number): string => {
  const start = hexToRgb(from);
  const end = hexToRgb(to);

  const r = Math.round(start.r + (end.r - start.r) * t);
  const g = Math.round(start.g + (end.g - start.g) * t);
  const b = Math.round(start.b + (end.b - start.b) * t);

  return rgbToHex(r, g, b);
};

const colorForRatio = (ratio: number): string => {
  const clamped = Math.min(Math.max(ratio, 0), 1);
  if (clamped <= 1 / 3) {
    const t = clamped / (1 / 3);
    return interpolateColor(COLOR_STOP_1, COLOR_STOP_2, t);
  }
  if (clamped <= 2 / 3) {
    const t = (clamped - 1 / 3) / (1 / 3);
    return interpolateColor(COLOR_STOP_2, COLOR_STOP_3, t);
  }
  const t = (clamped - 2 / 3) / (1 / 3);
  return interpolateColor(COLOR_STOP_3, COLOR_STOP_4, t);
};

const polarToCartesian = (angleInDegrees: number) => {
  const angleInRadians = (Math.PI / 180) * angleInDegrees;
  return {
    x: CENTER + RADIUS * Math.cos(angleInRadians),
    y: CENTER + RADIUS * Math.sin(angleInRadians),
  };
};

const describeArc = (startAngle: number, endAngle: number) => {
  const start = polarToCartesian(startAngle);
  const end = polarToCartesian(endAngle);
  const deltaAngle = endAngle - startAngle;
  const largeArcFlag = deltaAngle > 180 ? 1 : 0;

  return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
};

interface MoodMetricGaugeProps {
  label: string;
  value: number | null | undefined;
  feedback?: string | null;
  className?: string;
}

export function MoodMetricGauge({ label, value, feedback, className }: MoodMetricGaugeProps) {
  const baseId = useId();

  const { displayValue, scaleKey, segments } = useMemo(() => {
    if (value == null || Number.isNaN(value)) {
      return {
        displayValue: '—',
        scaleKey: 'unknown' as MoodScale,
        segments: [] as GradientSegment[],
      };
    }

    const normalized = clampValue(value);
    const progressRatio = normalized / 10;
    const scaleKey = resolveScale(normalized);

    if (progressRatio <= 0) {
      return {
        displayValue: normalized.toString(),
        scaleKey,
        segments: [] as GradientSegment[],
      };
    }

    const activeSegments: GradientSegment[] = [];

    for (let index = 0; index < SEGMENTS; index += 1) {
      const segmentStartRatio = index / SEGMENTS;
      const segmentEndRatio = (index + 1) / SEGMENTS;

      if (progressRatio <= segmentStartRatio) {
        break;
      }

      const effectiveEndRatio = Math.min(progressRatio, segmentEndRatio);
      const startAngle = START_ANGLE + segmentStartRatio * ARC_RANGE;
      const endAngle = START_ANGLE + effectiveEndRatio * ARC_RANGE;
      const coveredAngle = endAngle - startAngle;

      if (coveredAngle <= 0) {
        continue;
      }

      const startPoint = polarToCartesian(startAngle);
      const endPoint = polarToCartesian(endAngle);

      activeSegments.push({
        id: `${baseId}-segment-${index}`,
        path: describeArc(startAngle, endAngle),
        x1: startPoint.x,
        y1: startPoint.y,
        x2: endPoint.x,
        y2: endPoint.y,
        startColor: colorForRatio(segmentStartRatio),
        endColor: colorForRatio(effectiveEndRatio),
      });

      if (progressRatio <= segmentEndRatio) {
        break;
      }
    }

    return {
      displayValue: normalized.toString(),
      scaleKey,
      segments: activeSegments,
    };
  }, [value, baseId]);

  const scaleConfig = SCALE_CONFIG[scaleKey];
  const trackPath = describeArc(START_ANGLE, END_ANGLE);

  return (
    <div
      className={cn(
        'flex flex-1 min-w-[180px] flex-col gap-4 rounded-2xl border border-primary/10 bg-primary/5 p-4',
        className,
      )}
    >
      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide">
        <span className="text-muted-foreground">{label}</span>
        <span style={{ color: scaleConfig.color }}>{scaleConfig.label}</span>
      </div>

      <div className="mt-4 flex justify-center">
        <div className="relative flex h-[108px] w-[108px] items-center justify-center">
          <svg width={GAUGE_SIZE} height={GAUGE_SIZE} viewBox={`0 0 ${GAUGE_SIZE} ${GAUGE_SIZE}`}>
            <defs>
              {segments.map(({ id, x1, y1, x2, y2, startColor, endColor }) => (
                <linearGradient
                  key={id}
                  id={id}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor={startColor} />
                  <stop offset="100%" stopColor={endColor} />
                </linearGradient>
              ))}
            </defs>

            <path
              d={trackPath}
              stroke="rgba(148, 163, 184, 0.35)"
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
              fill="none"
            />

            {segments.map(({ id, path }) => (
              <path
                key={id}
                d={path}
                stroke={`url(#${id})`}
                strokeWidth={STROKE_WIDTH}
                strokeLinecap="round"
                fill="none"
              />
            ))}
          </svg>
          <div className="pointer-events-none absolute flex flex-col items-center">
            <p className="text-4xl font-semibold text-foreground">{displayValue}</p>
            <p className="text-xs text-muted-foreground">su 10</p>
          </div>
        </div>
      </div>

      {feedback ? <p className="text-xs leading-5 text-muted-foreground/80">{feedback}</p> : null}
    </div>
  );
}
